/**
 * VersionHistoryPanel component.
 * Sidebar/expandable panel for viewing version history with rollback support.
 * Uses the useVersionHistory hook to fetch entries and provides rollback triggers.
 */

import type { ReactElement } from "react";
import { useState, useCallback } from "react";

import type { VersionHistoryEntry } from "../diff/types";

// ============================================================================
// Types
// ============================================================================

/** Props for VersionHistoryPanel component */
export interface VersionHistoryPanelProps {
  /** Version history entries */
  readonly entries: readonly VersionHistoryEntry[];
  /** Whether history is loading */
  readonly isLoading: boolean;
  /** Error message if loading failed */
  readonly error?: string | null | undefined;
  /** Current live version number */
  readonly liveVersion?: number | undefined;
  /** Pagination info */
  readonly pagination?:
    | {
        readonly total: number;
        readonly limit: number;
        readonly offset: number;
        readonly hasMore: boolean;
      }
    | null
    | undefined;
  /** Whether the panel is open */
  readonly isOpen: boolean;
  /** Callback to close the panel */
  readonly onClose: () => void;
  /** Callback when "Load More" is clicked */
  readonly onLoadMore?: (() => Promise<void>) | undefined;
  /** Callback when "Refresh" is clicked */
  readonly onRefresh: () => Promise<void>;
  /** Callback when rollback is requested for a version */
  readonly onRollback: (version: number) => void;
}

// ============================================================================
// Component
// ============================================================================

/**
 * VersionHistoryPanel — sidebar panel for viewing and managing config version history.
 */
export function VersionHistoryPanel({
  entries,
  isLoading,
  error,
  liveVersion,
  pagination,
  isOpen,
  onClose,
  onLoadMore,
  onRefresh,
  onRollback,
}: VersionHistoryPanelProps): ReactElement | null {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const handleRefresh = useCallback(async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefresh]);

  const handleLoadMore = useCallback(async (): Promise<void> => {
    if (!onLoadMore) return;
    setIsLoadingMore(true);
    try {
      await onLoadMore();
    } finally {
      setIsLoadingMore(false);
    }
  }, [onLoadMore]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-y-0 right-0 z-40 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl flex flex-col"
      data-testid="version-history-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Version History</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
            title="Refresh history"
          >
            <RefreshIcon className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close history panel"
          >
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Error state */}
        {error && (
          <div className="m-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            <button
              type="button"
              onClick={handleRefresh}
              className="mt-2 text-xs text-red-600 hover:text-red-700 dark:text-red-400"
            >
              Try again
            </button>
          </div>
        )}

        {/* Loading state */}
        {isLoading && entries.length === 0 && !error && (
          <div className="flex items-center justify-center py-16">
            <SpinnerIcon className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && entries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <HistoryIcon className="w-10 h-10 text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No version history</p>
          </div>
        )}

        {/* Version list */}
        {entries.length > 0 && (
          <div className="p-4 space-y-2">
            {entries.map((entry) => (
              <VersionEntry
                key={entry.version}
                entry={entry}
                isLive={entry.version === liveVersion}
                onRollback={onRollback}
              />
            ))}
          </div>
        )}

        {/* Load more */}
        {pagination?.hasMore && onLoadMore && (
          <div className="px-4 pb-4 text-center">
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              className="px-3 py-1.5 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 disabled:opacity-50 flex items-center gap-1 mx-auto"
            >
              {isLoadingMore && <SpinnerIcon className="w-3 h-3 animate-spin" />}
              {isLoadingMore ? "Loading..." : "Load More"}
            </button>
            <p className="text-xs text-gray-500 mt-1">
              Showing {entries.length} of {pagination.total}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface VersionEntryProps {
  readonly entry: VersionHistoryEntry;
  readonly isLive: boolean;
  readonly onRollback: (version: number) => void;
}

function VersionEntry({ entry, isLive, onRollback }: VersionEntryProps): ReactElement {
  const canRollback = !isLive && entry.status !== "draft";

  const borderColor = isLive
    ? "border-green-200 dark:border-green-800"
    : "border-gray-200 dark:border-gray-700";
  const bgColor = isLive ? "bg-green-50 dark:bg-green-900/10" : "bg-white dark:bg-gray-800";

  return (
    <div
      className={`p-3 rounded-lg border ${borderColor} ${bgColor}`}
      data-testid={`version-entry-${entry.version}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {entry.versionString || `v${entry.version}`}
          </span>
          <VersionStatusBadge status={entry.status} isLive={isLive} />
        </div>
        {canRollback && (
          <button
            type="button"
            onClick={() => onRollback(entry.version)}
            className="px-2 py-0.5 text-xs font-medium text-amber-600 hover:text-amber-700 dark:text-amber-400
                       hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded"
            title="Rollback to this version"
          >
            Rollback
          </button>
        )}
      </div>

      {entry.changeSummary && (
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 line-clamp-2">
          {entry.changeSummary}
        </p>
      )}

      <div className="text-xs text-gray-500 dark:text-gray-400">
        <span>{formatRelativeTime(entry.createdAt)}</span>
        <span className="mx-1">by</span>
        <span>{entry.createdBy}</span>
      </div>
    </div>
  );
}

interface VersionStatusBadgeProps {
  readonly status: VersionHistoryEntry["status"];
  readonly isLive: boolean;
}

function VersionStatusBadge({ status, isLive }: VersionStatusBadgeProps): ReactElement {
  const colors: Record<string, string> = {
    live: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    draft: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    archived: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
  };

  const displayStatus = isLive ? "live" : status;

  return (
    <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${colors[displayStatus] ?? ""}`}>
      {displayStatus}
    </span>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

// ============================================================================
// Icons
// ============================================================================

function CloseIcon({ className }: { readonly className: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function RefreshIcon({ className }: { readonly className: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

function SpinnerIcon({ className }: { readonly className: string }): ReactElement {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function HistoryIcon({ className }: { readonly className: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

export default VersionHistoryPanel;
